/**
 * QualificationRequisitionMaster Component
 * 
 * PURPOSE:
 * Main container component for managing welder qualification requisitions
 * Provides tabbed interface for different qualification workflow states
 * 
 * FUNCTIONALITY:
 * 1. Displays qualification requests in tabbed format (Initiated/Pending Approval)
 * 2. Provides global search across all qualification data
 * 3. Handles Excel export with role-based data access
 * 4. Manages add/edit operations for qualification requests
 * 5. Coordinates between different qualification workflow components
 * 
 * TABS:
 * - Tab 1: Initiated Requests - Requests created by current user
 * - Tab 2: Pending Approval - Requests pending QCE approval
 * 
 * ROLE-BASED ACCESS:
 * - QCE (Quality Control Engineer): Can view/approve all requests, export all data
 * - Other Users: Can view own requests, limited export access
 * 
 * INTEGRATION:
 * - QualificationReqInitiated: Handles user's own requests
 * - QualificationReqApproval: Handles approval workflow for QCE
 * - AddEditQualificationRequisition: Modal for creating/editing requests
 * 
 * EXPORT FEATURES:
 * - Comprehensive Excel export with all qualification fields
 * - Role-based data filtering
 * - Calculated fields (pending duration, cycle time)
 * 
 * USED BY:
 * - Welder qualification management workflows
 * - Quality control approval processes
 * - Qualification tracking and reporting
 */

import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import GolbalSearch from "../utility/common/helpers/GolbalSearch";
import { handleExport1 } from "../utility/data/export/GlobalExport";
import { Row, Col, Container, Tabs, Tab } from "react-bootstrap";
import Export from "../../icons/Export.svg";
import Toaster from "../utility/ui/notifications/Toaster";
import { QualificationReqApproval } from "./QualificationReqApproval";
import { QualificationReqInitiated } from "./QualificationReqInitiated";
import AddEditQualificationRequisition from "./AddEditQualificationRequisition";
import { getAllDataQCEExport } from "../../Services/trainingRequisition-service";
import { DateDiff, FormatYYYYMMDD, getDefaultDate } from "../../functions/welding-functions";

/**
 * Main qualification requisition component with forwardRef for parent access
 * 
 * @param {Object} props - Component props
 * @param {Object} ref - Forward ref for parent component access
 */
const QualificationRequisition = forwardRef((props, ref) => {
  // Tab management state
  const [key, setKey] = useState(1); // Active tab key (1: Initiated, 2: Approval)
  const [refe, setRefe] = useState(); // Reference for global search
  
  // Data management state
  const [excelData1, setExcelData] = useState([]); // Excel export data
  const [showForm, setShowForm] = useState(false); // Add/Edit form visibility
  const [action, setAction] = useState(""); // Current action (Add/Edit)
  const [mainReload, setMainReload] = useState(false); // Reload trigger for child components
  const [rowData, setRowData] = useState({}); // Selected row data for editing
  const [calledFrom, setCalledFrom] = useState(""); // Component call source tracking
  
  // User session data
  const [username] = useState(sessionStorage.getItem("UserName"));
  
  // Child component references
  const Qualificationinit = useRef(); // Reference to initiated requests component
  const Qualificationpend = useRef(); // Reference to pending approval component
  /**
   * Grid column definitions for qualification requisition display
   * 
   * COLUMNS:
   * - Status: Workflow status with custom renderer
   * - Request Number: Unique qualification request identifier
   * - Employee Code: Welder's employee/salary code
   * - Welder Name: Name with avatar display
   * - Process: Welding process qualified for
   * - Position: Welding positions qualified (F, H, V, O, SP, 6G, 5G)
   * - Material: Base material specification
   * - Company: Welder's company
   * - Dates: Welding date, approval/rejection date
   * - Metrics: Pending duration and cycle time
   * 
   * FEATURES:
   * - Custom cell renderers for status and avatar
   * - Sortable status column (ascending by default)
   * - Numeric type for calculated fields
   * - Responsive column widths
   */
  const tmpcolumnDefs = [
    {
      headerName: "Status",
      field: "wld_status",
      type: "Text",
      isCheckbox: false,
      cellRenderer: "trainingReqCellRenderer", // Custom status renderer
      width: 50,
      sortable: true,
      sort: 'asc', // Default ascending sort
    },
    {
      headerName: "Welder Qualification Req",
      field: "wld_TrainingReqNumber", // Unique request identifier
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "Salary/Employee Code",
      field: "wld_TrainingReqSalaryEmployeeCode", // Employee identification
      isCheckbox: false,
      cellRenderer: "",
      width: 50,
    },
    {
      headerName: "Welder Name",
      field: "wld_TrainingReqEmployeeName", // Welder identification
      isCheckbox: false,
      cellRenderer: "AvatarCellRenderer", // Avatar display
    },
    {
      headerName: "Welder Process",
      field: "wld_ProcessQualified", // Welding process (SMAW, GTAW, etc.)
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "Welder Position",
      field: "wld_PositionQualified1", // Qualified positions
      isCheckbox: false,
    },
    {
      headerName: "Qualification Material",
      field: "wld_BaseMaterial", // Base material specification
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "Company Name",
      field: "wld_TrainingReqCompanyName", // Welder's company
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "Welding Date",
      field: "wld_TrainingReqDate", // Date of welding test
      isCheckbox: false,
      cellRenderer: "",
    },
    {
      headerName: "Approved/Rejected Date",
      field: "wld_ApprovedRejectedQualDate", // Decision date
      isCheckbox: false,
      cellRenderer: "",
      width: 250,
    },
    {
      headerName: "Welder Qualification Pending Since",
      field: "wld_pendingSince", // Days pending (calculated)
      isCheckbox: false,
      cellRenderer: "",
      width: 250,
      type: "Number" // Numeric sorting
    },
    {
      headerName: "Qualification Total Cycle Time",
      field: "wld_CycleTime", // Total process time (calculated)
      isCheckbox: false,
      cellRenderer: "",
      width: 50,
      type: "Number" // Numeric sorting
    },
  ];

  /**
   * Global search handler
   * 
   * FUNCTIONALITY:
   * - Applies search filter across all qualification data
   * - Searches through status, request numbers, employee codes, names, etc.
   * - Updates grid display in real-time
   * 
   * @param {string} text - Search text entered by user
   */
  const handleGlobalSearch = (text) => {
    refe.current.setSearch(text);
  };

  /**
   * Excel export handler with role-based data access
   * 
   * FUNCTIONALITY:
   * - QCE Role: Exports all qualification data from database
   * - Other Roles: Exports currently displayed grid data
   * - Includes calculated fields (pending duration, cycle time)
   * - Comprehensive column set with all qualification details
   * 
   * EXPORT COLUMNS:
   * - Basic info: Status, request number, dates, employee details
   * - Technical: Process, position, material, joint category
   * - Workflow: Supervisor, TPI, examination results
   * - Metrics: Pending duration, cycle time calculations
   */
  const handleGlobalExport = async () => {
    let AllResult;
    const allColumnDefs = [] // Excel column definitions

    // Add a column for WPQ_WelderName
    allColumnDefs.push({
      headerName: 'Status',
      field: 'wld_status',
    });

    allColumnDefs.push({
      headerName: 'Welder Qualification Req',
      field: 'wld_TrainingReqNumber',
    });

    allColumnDefs.push({
      headerName: 'Requisition Date',
      field: 'wld_TrainingReqDate',
    });

    allColumnDefs.push({
      headerName: 'Salary/Employee Code',
      field: 'wld_TrainingReqSalaryEmployeeCode',
    });

    allColumnDefs.push({
      headerName: 'Welder Name',
      field: 'wld_TrainingReqEmployeeName',
    });

    allColumnDefs.push({
      headerName: 'Employee Type',
      field: 'wld_TrainingReqEmployeeType',
    });

    allColumnDefs.push({
      headerName: 'Welder Process',
      field: 'wld_ProcessQualified',
    });

    allColumnDefs.push({
      headerName: 'Welder Position',
      field: 'wld_PositionQualified1',
    });

    allColumnDefs.push({
      headerName: 'Qualification Material',
      field: 'wld_BaseMaterial',
    });

    allColumnDefs.push({
      headerName: 'Joint Category',
      field: 'wld_JointCategory',
    });


    allColumnDefs.push({
      headerName: 'Assigning Dept',
      field: 'wld_AssgnDept',
    });

    allColumnDefs.push({
      headerName: 'Godrej Supervisor Name',
      field: 'wld_SupervisorName',
    });

    allColumnDefs.push({
      headerName: 'Godrej Supervisor Date',
      field: 'wld_TrainingReqDate',
    });


    allColumnDefs.push({
      headerName: 'TPI',
      field: 'wld_Tpi',
    });

    allColumnDefs.push({
      headerName: 'Company Name',
      field: 'wld_TrainingReqCompanyName',
    });

    allColumnDefs.push({
      headerName: 'Welding Date',
      field: 'wld_TrainingReqDate',
    });

    allColumnDefs.push({
      headerName: 'Visual Examination',
      field: 'wld_VisualExam',
    });

    allColumnDefs.push({
      headerName: 'Visual Examination Date',
      field: 'wld_VisualExamDate',
    });

    allColumnDefs.push({
      headerName: 'RT Offer Wrench',
      field: 'wld_RTOfferWrench',
    });

    allColumnDefs.push({
      headerName: 'RT Offer Wrench Date',
      field: 'wld_RTOfferWrenchDate',
    });

    allColumnDefs.push({
      headerName: 'NDE Examination',
      field: 'wld_NDEExam',
    });

    allColumnDefs.push({
      headerName: 'NDE Examination Date',
      field: 'wld_NDEExamDate',
    });

    allColumnDefs.push({
      headerName: 'Comments',
      field: 'wld_TrainingReqApproverFinalComment',
    });

    allColumnDefs.push({
      headerName: 'WPQ No',
      field: 'wld_WpqNo',
    });

    allColumnDefs.push({
      headerName: 'Approved/Rejected Date',
      field: 'wld_ApprovedRejectedQualDate',
    });

    allColumnDefs.push({
      headerName: 'Welder Qualification Pending Since',
      field: 'wld_pendingSince',
      type: "Number"

    });



    let filename = "QualificationRequisitionMaster";

    if (sessionStorage.getItem("Role") === process.env.REACT_APP_ROLE_QCENGINEER) {
      AllResult = await getAllDataQCEExport();
      AllResult.forEach((i) => {
        i.wld_pendingSince = DateDiff(FormatYYYYMMDD(i.wld_TrainingReqDate), getDefaultDate())
        i.wld_CycleTime = 0
      })
      allColumnDefs.push({
        headerName: 'Welder Qualification Pending Since',
        field: 'wld_pendingSince',
        type: "Number"

      });

      allColumnDefs.push({
        headerName: 'Qualification Total Cycle Time',
        field: 'wld_CycleTime',
        type: "Number"

      });

      handleExport1(AllResult, filename, allColumnDefs);
    }
    else {
      handleExport1(excelData1, filename, allColumnDefs);
    }


  };

  /**
   * Tab selection handler
   * 
   * FUNCTIONALITY:
   * - Switches between Initiated and Approval tabs
   * - Updates active tab state
   * - Triggers appropriate component rendering
   * 
   * @param {number} e - Selected tab key (1: Initiated, 2: Approval)
   */
  const handleSelect = (e) => {
    setKey(e);
  };
  
  /**
   * Main reload effect
   * 
   * FUNCTIONALITY:
   * - Triggers reload of both child components when mainReload changes
   * - Ensures data consistency across tabs
   * - Called after add/edit/delete operations
   */
  useEffect(() => {
    Qualificationinit.current.reload(); // Reload initiated requests
    Qualificationpend.current.reload(); // Reload pending approvals
  }, [mainReload]);

  /**
   * Imperative handle for parent component access
   * 
   * EXPOSED METHODS:
   * - handleAdd: Opens add new qualification form
   * 
   * FUNCTIONALITY:
   * - Allows parent components to trigger add operation
   * - Sets form to Add mode and displays modal
   * - Triggers component reload
   */
  useImperativeHandle(ref, () => ({
    handleAdd: async () => {
      setAction("Add");
      setShowForm(true);
      setMainReload(!mainReload);
    },
  }));

  /**
   * Form close callback
   * 
   * FUNCTIONALITY:
   * - Triggered when add/edit form is closed
   * - Refreshes component data
   * - Ensures UI reflects any changes made
   * 
   * @param {boolean} boolVar - Close flag (unused)
   */
  function funcclose(boolVar) {
    setMainReload(!mainReload);
  }

  return (
    <>
      <Container fluid>

        <Row className="RowHeightTREQ" style={{ position: "relative", top: "5px" }}>
          <Col md={2} sm={12} xs={12} className="ListTitle">
            Welder Qualification Request list
          </Col>
          <Col style={{ position: "relative", right: "5px" }}>
            <div className="global-search-container">
              <GolbalSearch
                searchplaceholdertext={
                  "Search for Status, WelderQualification Request No, Salary/Employee Code,Welder Name,etc"
                }
                filterFunc={handleGlobalSearch}
              />
              <img
                className="allexport"
                src={Export}
                alt="Export"
                onClick={handleGlobalExport}
              />
            </div>
          </Col>
        </Row>

        <Tabs
          style={{ position: "relative", top: "10px" }}
          activeKey={key}
          onSelect={(e) => handleSelect(e)}
          className="mb-3 w-100 border-end"
        >
          <Tab
            eventKey={1}
            title="Requests pending for your Approval"
            style={{
              alignItems: "left",
              height: window.innerHeight - 750,
            }}
          >
            <div className="wqrNote">NOTE: To Approve or Reject the Welder Qualification Request, please click on Welder Qualification Request Details Hyperlink</div>
            <div style={{ position: "relative", top: "12px", right: "15px" }}>
              <QualificationReqApproval
                tmpcolumnDefs={tmpcolumnDefs}
                setRefe={setRefe}
                key={key}
                close={funcclose}
                setExcelData={setExcelData}
                mainReload={mainReload}
                ref={Qualificationpend}
                showAddEditForm={() => setShowForm(true)}
                setShowForm={setShowForm}
                setAction={setAction}
                setRowData={setRowData}
                setCalledFrom={setCalledFrom}
              />
            </div>
          </Tab>

          <Tab
            eventKey={2}
            title="Requests sent for Approval"
            style={{
              height: window.innerHeight - 750,
            }}
          >
            <div className="wqrNote">NOTE: To Approve or Reject the Welder Qualification Request, please click on Welder Qualification Request Details Hyperlink</div>
            <div style={{ position: "relative", top: "12px", right: "15px" }}>
              <QualificationReqInitiated
                username={username}
                tmpcolumnDefs={tmpcolumnDefs}
                setRefe={setRefe}
                setExcelData={setExcelData}
                key={key}
                close={funcclose}
                mainReload={mainReload}
                ref={Qualificationinit}
                setShowForm={setShowForm}
                setAction={setAction}
                setRowData={setRowData}
                setCalledFrom={setCalledFrom}
              />
            </div>
          </Tab>
        </Tabs>

        {showForm && (
          <AddEditQualificationRequisition
            show={showForm}
            action={action}
            onHide={() => {
              setShowForm(false);
            }}
            setMainReload={setMainReload}
            mainReload={mainReload}
            rowData={rowData}
            calledFrom={calledFrom}
          />
        )}
        <Toaster />
      </Container>
    </>
  );
});

export default QualificationRequisition;
